import { useEffect, useState } from 'react'

import { ActionIcon, Collapse, Select } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import DNButton from 'src/components/Buttons/Button/DNButton'
import StyledReadOnlyInput from 'src/components/Inputs/StyledReadOnlyInput/StyledReadOnlyInput'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_COMPANY, UPDATE_COMPANY } from 'src/graphql/companies.graphql'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconEdit from 'src/icons/IconEdit'
import { formatInternationalPhoneNumber } from 'src/lib/phone.utils'

import FieldLabel from './FieldLabel'

interface CommunicationsCardProps {
  companyData: {
    companyId: string
    phoneNumber: string
    useRecruiterLine: boolean
    callRecordingsEnabled: boolean
    transcriptionEnabled: boolean
  }
  initialOpened?: boolean
}

const CommunicationsCard = ({
  companyData,
  initialOpened = false,
}: CommunicationsCardProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)
  const isRecruiter = currentUser?.roles?.includes('RECRUITER')
  const canEdit = isAdmin || isRecruiter

  const [values, setValues] = useState({ ...companyData })
  const [editing, setEditing] = useState(false)
  const [opened, setOpened] = useState(initialOpened)

  const [updateCompany, { loading }] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      toast('Communication settings updated successfully', 'success')
      setEditing(false)
    },
    onError: (error) => {
      toast(`Failed to update settings: ${error.message}`, 'error')
    },
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: { id: values.companyId },
      },
    ],
    awaitRefetchQueries: true,
  })

  // Sync internal state with parent data changes only when not editing
  useEffect(() => {
    if (!editing) {
      setValues({ ...companyData })
    }
  }, [companyData, editing])

  const handleEdit = () => {
    setEditing(true)
  }

  const handleCancel = () => {
    setValues({ ...companyData })
    setEditing(false)
  }

  const handleSave = async () => {
    const { companyId, phoneNumber: _phoneNumber, ...rest } = values

    await updateCompany({
      variables: {
        id: companyId,
        input: rest,
      },
    })
  }

  const doubleNickelPhoneNumberLabel = (
    <FieldLabel
      label="Double Nickel Phone Number"
      tooltip="The outgoing and incoming Double Nickel phone number"
    />
  )
  const incomingCallRoutingLabel = (
    <FieldLabel
      label="Incoming Call Routing"
      tooltip="Route incoming calls to the specified phone number"
    />
  )
  const callTranscriptionLabel = (
    <FieldLabel
      label="Call Transcription"
      tooltip="Automatically transcribe incoming and outgoing calls"
    />
  )
  const callRecordingLabel = (
    <FieldLabel
      label="Call Recording"
      tooltip="Automatically record incoming and outgoing calls"
    />
  )

  return (
    <div
      className={`flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3`}
    >
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Communications
        {canEdit && !editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              if (!opened) setOpened(true)
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {canEdit && editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={loading}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={loading}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-2">
          <div className={`grid grid-cols-12 gap-3`}>
            {/* Double Nickel Phone Number - Always Read-only */}
            <div className="col-span-3">
              <StyledReadOnlyInput
                label={doubleNickelPhoneNumberLabel}
                value={
                  formatInternationalPhoneNumber(values.phoneNumber) || '-'
                }
              />
            </div>

            {/* Use Recruiter Line */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={incomingCallRoutingLabel}
                  value={values.useRecruiterLine ? 'yes' : 'no'}
                  onChange={(value) =>
                    setValues({
                      ...values,
                      useRecruiterLine: value === 'yes',
                    })
                  }
                  data={[
                    { value: 'yes', label: 'Company General Phone Number' },
                    { value: 'no', label: "Assigned Employee's Phone Number" },
                  ]}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={incomingCallRoutingLabel}
                  value={
                    values.useRecruiterLine
                      ? 'Company General Phone Number'
                      : "Assigned Employee's Phone Number"
                  }
                />
              )}
            </div>

            {/* Transcription */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={callTranscriptionLabel}
                  value={values.transcriptionEnabled ? 'yes' : 'no'}
                  onChange={(value) =>
                    setValues({
                      ...values,
                      transcriptionEnabled: value === 'yes',
                    })
                  }
                  data={[
                    { value: 'yes', label: 'Enabled' },
                    { value: 'no', label: 'Disabled' },
                  ]}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={callTranscriptionLabel}
                  value={values.transcriptionEnabled ? 'Enabled' : 'Disabled'}
                />
              )}
            </div>

            {/* Call Recordings */}
            <div className="col-span-3">
              {editing ? (
                <Select
                  label={callRecordingLabel}
                  value={values.callRecordingsEnabled ? 'yes' : 'no'}
                  onChange={(value) =>
                    setValues({
                      ...values,
                      callRecordingsEnabled: value === 'yes',
                    })
                  }
                  data={[
                    { value: 'yes', label: 'Enabled' },
                    { value: 'no', label: 'Disabled' },
                  ]}
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                  }}
                />
              ) : (
                <StyledReadOnlyInput
                  label={callRecordingLabel}
                  value={values.callRecordingsEnabled ? 'Enabled' : 'Disabled'}
                />
              )}
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  )
}

export default CommunicationsCard
